import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextareaAutosize,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  FormLabel,
  Grid,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import { ReactElement, useState } from "react";
import { TypeCommon } from "models/common";
import "./index.scss";
import { dE } from "@fullcalendar/core/internal-common";

interface BaseConfirmDialogProps {
  open: boolean;
  title: string;
  content: string | ReactElement;
  onClose: () => void;
  onConfirm: () => void;
  confirmText?: string;
  cancelText?: string;
  confirmButtonColor?:
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning"
    | "inherit";
  cancelButtonColor?:
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning"
    | "inherit";
  loading?: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  fullWidth?: boolean;
  showTextarea?: boolean;
  textareaLabel?: string;
  textareaPlaceholder?: string;
  textareaValue?: string;
  onTextareaChange?: (value: string) => void;
  textareaRequired?: boolean;
  showAutocomplete?: boolean;
  autocompleteLabel?: string;
  autocompletePlaceholder?: string;
  autocompleteOptions?: Array<{ label: string; value: number}>;
  autocompleteValue?: number;
  onAutocompleteChange?: (value: number) => void;
  autocompleteRequired?: boolean;
  autocompleteGetOptionLabel?: (option: number) => string;
  autocompleteRenderOption?: (props: TypeCommon, option: number) => ReactElement;
  showSelectTags?: boolean;
  selectTagsOptions?: Array<{ name: string; id: number}>;
  selectTagsValue?: Array<{ name: string; id: number}>;
  onSelectTagsChange?: (value: Array<{ name: string; id: number}>) => void;
  selectTagsRequired?: boolean;
  selectTagsFixedOptions?: Array<{ name: string; id: number}>;
  showSelectHonbu?: boolean;
  selectHonbuPlaceholder?: string;
  selectHonbuOptions?: Array<{ name: string; id: number; organization_id: number}>;
  selectHonbuValue?: number;
  onSelectHonbuChange?: (value: { id: number; organization_id: number }) => void;
  selectHonbuRequired?: boolean;
  selectedDataUserOptions?: Array<{ name: string; id: number}>;
}

const BaseConfirmDialog = ({
  open,
  title,
  content,
  onClose,
  onConfirm,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmButtonColor = "primary",
  cancelButtonColor = "inherit",
  loading = false,
  maxWidth = "sm",
  fullWidth = true,
  showTextarea = false,
  textareaLabel = "Reason",
  textareaPlaceholder = "Enter reason here...",
  textareaValue = "",
  onTextareaChange,
  textareaRequired = false,
  showAutocomplete = false,
  autocompleteLabel = "Select an option",
  autocompletePlaceholder = "Choose an option",
  autocompleteOptions = [],
  autocompleteValue,
  onAutocompleteChange,
  autocompleteRequired = false,
  autocompleteGetOptionLabel,
  autocompleteRenderOption,
  showSelectTags = false,
  selectTagsOptions = [],
  selectTagsValue = [],
  onSelectTagsChange,
  selectTagsRequired = false,
  selectTagsFixedOptions = [],
  selectHonbuOptions = [],
  selectHonbuValue,
  onSelectHonbuChange,
  selectHonbuRequired = false,
}: BaseConfirmDialogProps): ReactElement => {
  const [isShowSelectHonbu, setIsShowSelectHonbu] = useState<boolean>(false);
  const CheckboxesTags = (): ReactElement => {
    return (
      <Grid container spacing={2} className="mt-2">
        <Grid size={12}>
          <Autocomplete
            disableCloseOnSelect
            multiple
            fullWidth
            id="checkboxes-tags-demo"
            options={selectTagsOptions}
            getOptionLabel={(option) => option.name}
            value={selectTagsValue}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            className="checkboxes-tags-autocomplete"
            onChange={(event, newValue) => {
                // Cập nhật state local để hiển thị/ẩn Select Honbu
                handleSelectTagsChange(newValue);
                if (selectTagsFixedOptions.length > 0) {
                  // Nếu có fixed options, giữ chúng và thêm options mới
                  const result = [
                    ...selectTagsFixedOptions,
                    ...newValue.filter((option) => !selectTagsFixedOptions.includes(option))
                  ];
                  onSelectTagsChange?.(result);
                } else {
                  onSelectTagsChange?.(newValue);
                }
              }}
            
            renderValue={(values, getItemProps) =>
              values.map((option, index) => {
                const { key, ...itemProps } = getItemProps({ index });
                return (
                  <Chip
                    key={key}
                    label={option.name}
                    {...itemProps}
                    onDelete={() => {
                      const newValue = values.filter((_, i) => i !== index);
                      onSelectTagsChange?.(newValue);
                      // Cập nhật state local để trigger onChange
                      handleSelectTagsChange(newValue);
                    }}
                    style={{ margin: '2px' }}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField {...params} label="対象承認組織" className="checkboxes-tags-input"/>
            )}
          />
        </Grid>
      </Grid>
    );
  }

  const handleSelectTagsChange = (value: Array<{ name: string; id: number}>): void => {
    setIsShowSelectHonbu(value.some(option => option.id === 3));
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      className="base-confirm-dialog"
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {typeof content === "string" ? (
          <DialogContentText>{content}</DialogContentText>
        ) : (
          content
        )}
        {showTextarea && (
          <div className="textarea-container mt-2">
            <TextareaAutosize
              minRows={3}
              placeholder={textareaPlaceholder}
              className={`custom-textarea`}
              value={textareaValue}
              onChange={(e) => onTextareaChange?.(e.target.value)}
              required={textareaRequired}
              maxLength={1000}
            />
          </div>
        )}
        {showAutocomplete && (
          <div className="autocomplete-container mt-2">
            <Autocomplete
              options={autocompleteOptions.map((option) => option.value)}
              getOptionLabel={autocompleteGetOptionLabel}
              renderOption={autocompleteRenderOption}
              value={autocompleteValue}
              noOptionsText="データがありません"
              onChange={(event, newValue) => {
                onAutocompleteChange?.(newValue || -1);
              }}
              renderInput={(params) => (
                <TextField
                  
                  {...params}
                  label={autocompleteLabel}
                  placeholder={autocompletePlaceholder}
                  required={autocompleteRequired}
                  className="autocomplete-input-confirm-dialog"
                />
              )}
            />
          </div>
        )}
        {showSelectTags && (
          <div className="checkboxes-tags-container mt-2">
            <CheckboxesTags />
          </div>
        )}
        {isShowSelectHonbu && (
          <Grid container spacing={2} className="mt-2">
            <Grid size={12}>
              <FormLabel>承認本部組織</FormLabel>
              <Select
                value={selectHonbuValue || ""}
                onChange={(e) => {
                  const selectedValue = Number(e.target.value);
                  if (selectedValue > 0) {
                    const selectedOption = selectHonbuOptions?.find(option => option.id === selectedValue);
                    if (selectedOption) {
                      onSelectHonbuChange?.({
                        id: selectedOption.id,
                        organization_id: selectedOption.organization_id
                      });
                    }
                  }
                }}
                displayEmpty
                fullWidth
                required={selectHonbuRequired}
                error={selectHonbuRequired && (!selectHonbuValue || selectHonbuValue <= 0)}
                endAdornment={
                  selectHonbuValue && selectHonbuValue > 0 ? (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectHonbuChange?.({ id: 0, organization_id: 0 });
                      }}
                      sx={{ marginRight: 1.5 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : null
                }
              >
                <MenuItem value="">
                  <span style={{ color: 'text.disabled', opacity: 0.6 }}>承認本部組織を選択</span>
                </MenuItem>
                {selectHonbuOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
              {selectHonbuRequired && (!selectHonbuValue || selectHonbuValue <= 0) && (
                <div style={{ color: 'red', fontSize: '0.75rem', marginTop: '4px' }}>
                  承認本部組織を選択してください
                </div>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color={cancelButtonColor} disabled={loading}>
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          color={confirmButtonColor}
          variant="contained"
          disabled={
            loading ||
            (showTextarea &&
              textareaRequired &&
              !textareaValue?.toString()?.trim()) ||
            (showAutocomplete &&
              autocompleteRequired &&
              !autocompleteValue) ||
            (isShowSelectHonbu &&
              selectHonbuRequired &&
              (!selectHonbuValue || selectHonbuValue <= 0)) ||
            (showSelectTags &&
              selectTagsRequired &&
              (!selectTagsValue || selectTagsValue.length === 0))
          }
          autoFocus
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BaseConfirmDialog;
